import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, RefreshCw, Loader2, Check, X, Lock, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import useAuth, { SUPERUSER_BYPASS_EMAIL, SUPERUSER_BYPASS_ENABLED } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { z } from 'zod';
import { profileCache } from '@/lib/profileCache';
import { formatDistanceToNow } from 'date-fns';
import { useLocationSharingContext } from '@/contexts/LocationSharingContext';
import AppInfo from '@/components/AppInfo';
import { UserSearch } from '@/components/UserSearch';

const profileSchema = z.object({
  username: z.string()
    .trim()
    .min(3, 'Username must be at least 3 characters')
    .max(30, 'Username must be less than 30 characters')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, underscores, and hyphens')
    .optional(),
  name: z.string().trim().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  status: z.string().trim().max(150, 'Status must be less than 150 characters').optional(),
});

const AVATAR_STYLES = ['adventurer', 'avataaars', 'bottts', 'lorelei', 'micah', 'personas'];

const STATUS_OPTIONS = [
  { label: 'Looking for gifts 🎁', value: 'Looking for gifts 🎁' },
  { label: 'Available to help 💝', value: 'Available to help 💝' },
  { label: 'Busy right now ⏰', value: 'Busy right now ⏰' },
  { label: 'Busking 🎸', value: 'Busking 🎸' },
  { label: 'Entertaining 🎭', value: 'Entertaining 🎭' },
];

const BYPASS_PROFILE_STORAGE_KEY = 'giftmap_superuser_profile';

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { privacyLevel, setPrivacyLevel } = useLocationSharingContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [username, setUsername] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [name, setName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [status, setStatus] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [radiusLimit, setRadiusLimit] = useState<number>(5);
  
  const [randomAvatars, setRandomAvatars] = useState<string[]>([]);
  const [selectedAvatar, setSelectedAvatar] = useState<number | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  
  const [canChangeUsername, setCanChangeUsername] = useState(false);
  const [usernameLastChanged, setUsernameLastChanged] = useState<Date | null>(null);
  const [canChangeName, setCanChangeName] = useState(false);
  const [nameLastChanged, setNameLastChanged] = useState<Date | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  
  // Account deletion state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Blocked users state
  const [blockedUsers, setBlockedUsers] = useState<Array<{ id: string; name: string; username: string | null; avatar_url: string | null }>>([]);
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const isBypassSuperuser = !!user?.email && SUPERUSER_BYPASS_ENABLED && user.email.toLowerCase() === SUPERUSER_BYPASS_EMAIL;

  // Fetch current user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('username, name, avatar_url, privacy_level, status, username_last_changed_at, name_last_changed_at, radius_limit_miles')
          .eq('id', user.id)
          .maybeSingle();
        
        if (error) throw error;
        
        if (data) {
          setUsername(data.username || '');
          setOriginalUsername(data.username || '');
          setName(data.name || '');
          setOriginalName(data.name || '');
          setStatus(data.status || '');
          setAvatarUrl(data.avatar_url || '');
          setPrivacyLevel(data.privacy_level || 'precise');
          setRadiusLimit(data.radius_limit_miles || 5);
          setUsernameLastChanged(data.username_last_changed_at ? new Date(data.username_last_changed_at) : null);
          setNameLastChanged(data.name_last_changed_at ? new Date(data.name_last_changed_at) : null);
          
          // Check if can change username
          const { data: canChangeUser } = await supabase.rpc('can_change_username', { user_id: user.id });
          setCanChangeUsername(!!canChangeUser);

          const { data: canChangeNm } = await supabase.rpc('can_change_name', { user_id: user.id });
          setCanChangeName(!!canChangeNm);
        } else if (isBypassSuperuser) {
          const storedProfile = (() => {
            try {
              const raw = localStorage.getItem(BYPASS_PROFILE_STORAGE_KEY);
              return raw ? JSON.parse(raw) : null;
            } catch {
              return null;
            }
          })();

          const fallbackAvatar = `https://api.dicebear.com/7.x/personas/svg?seed=${encodeURIComponent(user.email || 'superuser')}`;
          const initialUsername = storedProfile?.username || 'superuser';
          const initialName = storedProfile?.name || 'Superuser';
          const initialStatus = storedProfile?.status || '';
          const initialAvatar = storedProfile?.avatar_url || fallbackAvatar;

          setUsername(initialUsername);
          setOriginalUsername(initialUsername);
          setName(initialName);
          setOriginalName(initialName);
          setStatus(initialStatus);
          setAvatarUrl(initialAvatar);
          setCanChangeUsername(true);
          setCanChangeName(true);
        }
      } catch (error) {
        if (isBypassSuperuser) {
          return;
        }
        console.error('Error fetching profile:', error);
        toast({
          title: 'Failed to load profile',
          description: 'Please try again',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user]);

  // Fetch blocked users for the current user
  useEffect(() => {
    const loadBlocked = async () => {
      if (!user?.id) return;
      if (isBypassSuperuser) {
        setBlockedUsers([]);
        setBlockedLoading(false);
        return;
      }
      setBlockedLoading(true);
      try {
        // 1) Get blocked ids
        const { data: blocks, error: blocksErr } = await supabase
          .from('user_blocks')
          .select('blocked_id')
          .eq('blocker_id', user.id);
        if (blocksErr) throw blocksErr;

        const ids = (blocks || []).map((b: any) => b.blocked_id).filter(Boolean);
        if (ids.length === 0) {
          setBlockedUsers([]);
          return;
        }

        // 2) Fetch user details for those ids
        const { data: usersData, error: usersErr } = await supabase
          .from('users')
          .select('id, name, username, avatar_url')
          .in('id', ids);
        if (usersErr) throw usersErr;

        setBlockedUsers((usersData || []).map((u: any) => ({
          id: u.id,
          name: u.name,
          username: u.username,
          avatar_url: u.avatar_url,
        })));
      } catch (e) {
        console.error('Failed to load blocked users', e);
      } finally {
        setBlockedLoading(false);
      }
    };
    loadBlocked();
  }, [user?.id, isBypassSuperuser]);

  // Generate random avatars
  const generateRandomAvatars = () => {
    const seed = Date.now();
    const avatars = AVATAR_STYLES.map((style, idx) => 
      `https://api.dicebear.com/7.x/${style}/svg?seed=${seed + idx}`
    );
    setRandomAvatars(avatars);
    setSelectedAvatar(null);
  };

  useEffect(() => {
    generateRandomAvatars();
  }, []);

  // Check username availability with debounce (only if username changed)
  useEffect(() => {
    if (username === originalUsername) {
      setUsernameAvailable(true);
      return;
    }

    const checkUsernameAvailability = async () => {
      if (username.length < 3) {
        setUsernameAvailable(null);
        return;
      }

      if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
        setUsernameAvailable(false);
        return;
      }

      setCheckingUsername(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('id')
          .eq('username', username.toLowerCase())
          .maybeSingle();

        setUsernameAvailable(!data && !error);
      } catch (error) {
        console.error('Username check error:', error);
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    };

    const timeoutId = setTimeout(checkUsernameAvailability, 500);
    return () => clearTimeout(timeoutId);
  }, [username, originalUsername]);

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${user?.id}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setAvatarUrl(publicUrl);
      setUploadedFile(file);
      setSelectedAvatar(null);
      
      toast({
        title: 'Image uploaded',
        description: 'Your profile picture has been uploaded',
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Upload failed',
        description: 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle random avatar selection
  const handleRandomAvatarSelect = (index: number) => {
    setSelectedAvatar(index);
    setAvatarUrl(randomAvatars[index]);
    setUploadedFile(null);
  };

  const getNextUsernameChangeDate = () => {
    if (!usernameLastChanged) return null;
    const nextChange = new Date(usernameLastChanged);
    nextChange.setFullYear(nextChange.getFullYear() + 1);
    return nextChange;
  };

  const getNextNameChangeDate = () => {
    if (!nameLastChanged) return null;
    const nextChange = new Date(nameLastChanged);
    nextChange.setFullYear(nextChange.getFullYear() + 1);
    return nextChange;
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'deleteaccount') {
      toast({
        title: 'Confirmation required',
        description: 'Please type "deleteaccount" to confirm.',
        variant: 'destructive',
      });
      return;
    }

    setIsDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('delete-account', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to delete account');
      }

      // Sign out and clear local storage
      await supabase.auth.signOut();
      localStorage.clear();
      
      toast({
        title: 'Account deleted',
        description: 'Your account has been permanently deleted.',
      });

      // Redirect to landing page
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: 'Deletion failed',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate fields
    const validation = profileSchema.safeParse({ username, name, status });
    if (!validation.success) {
      toast({
        title: 'Validation error',
        description: validation.error.errors[0].message,
        variant: 'destructive',
      });
      return;
    }

    // Check username availability if changed
    if (username !== originalUsername && !usernameAvailable) {
      toast({
        title: 'Username unavailable',
        description: 'This username is already taken. Please choose another one.',
        variant: 'destructive',
      });
      return;
    }

    // Check if can change username if changed
    if (username !== originalUsername && !canChangeUsername) {
      toast({
        title: 'Cannot change username',
        description: 'You can only change your username once per year.',
        variant: 'destructive',
      });
      return;
    }

    // Check if can change display name if changed
    if (name.trim() !== originalName.trim() && !canChangeName) {
      toast({
        title: 'Cannot change display name',
        description: 'You can only change your display name once per year.',
        variant: 'destructive',
      });
      return;
    }

    if (!avatarUrl) {
      toast({
        title: 'Avatar required',
        description: 'Please select or upload a profile picture',
        variant: 'destructive',
      });
      return;
    }

    if (isBypassSuperuser) {
      const bypassProfile = {
        username: username.trim().toLowerCase(),
        name: name.trim(),
        status: status.trim() || null,
        avatar_url: avatarUrl,
        privacy_level: privacyLevel,
        radius_limit_miles: radiusLimit,
        updated_at: new Date().toISOString(),
      };

      try {
        localStorage.setItem(BYPASS_PROFILE_STORAGE_KEY, JSON.stringify(bypassProfile));
        localStorage.setItem('giftmap_profile_complete', 'true');
      } catch {}

      profileCache.clear();
      if (user?.id) {
        profileCache.set({
          id: user.id,
          username: bypassProfile.username,
          name: bypassProfile.name,
          avatar_url: bypassProfile.avatar_url,
        });
      }

      setOriginalUsername(bypassProfile.username);
      setOriginalName(bypassProfile.name);

      toast({
        title: 'Profile updated',
        description: 'Bypass profile saved on this device',
      });

      navigate('/dashboard');
      return;
    }

    setSaving(true);
    try {
      const updateData: any = {
        name: name.trim(),
        status: status.trim() || null,
        avatar_url: avatarUrl,
        privacy_level: privacyLevel,
        radius_limit_miles: radiusLimit,
      };

      // Only update username fields if username changed
      if (username !== originalUsername) {
        updateData.username = username.trim().toLowerCase();
        updateData.username_last_changed_at = new Date().toISOString();
      }

      // If display name changed, set name_last_changed_at
      // Compare trimmed values to avoid false positives
      if (name.trim() !== originalName.trim()) {
        updateData.name_last_changed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', user?.id);

      if (error) throw error;

      // Clear cache and update with new data
      profileCache.clear();
      profileCache.set({
        id: user!.id,
        username: username.trim().toLowerCase(),
        name: name.trim(),
        avatar_url: avatarUrl,
      });
      if (username.trim()) {
        localStorage.setItem('giftmap_profile_complete', 'true');
      }

      toast({
        title: 'Profile updated',
        description: 'Your changes have been saved successfully',
      });
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Update failed',
        description: 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Block a user by id (from search)
  const handleBlockUser = async (targetUserId: string) => {
    if (!user?.id || !targetUserId || targetUserId === user.id) return;
    setBlocking(true);
    try {
      const { error } = await supabase
        .from('user_blocks')
        .insert({ blocker_id: user.id, blocked_id: targetUserId });
      if (error) throw error;
      // Refresh list
      const { data } = await supabase
        .from('users')
        .select('id, name, username, avatar_url')
        .eq('id', targetUserId)
        .maybeSingle();
      if (data) {
        setBlockedUsers(prev => prev.find(u => u.id === targetUserId) ? prev : [...prev, data]);
      }
      toast({ title: 'User blocked', description: 'They will no longer appear for you.' });
    } catch (e) {
      console.error('Block failed', e);
      toast({ title: 'Failed to block user', description: 'Please try again', variant: 'destructive' });
    } finally {
      setBlocking(false);
    }
  };

  // Unblock a user
  const handleUnblockUser = async (targetUserId: string) => {
    if (!user?.id || !targetUserId) return;
    try {
      const { error } = await supabase
        .from('user_blocks')
        .delete()
        .eq('blocker_id', user.id)
        .eq('blocked_id', targetUserId);
      if (error) throw error;
      setBlockedUsers(prev => prev.filter(u => u.id !== targetUserId));
      toast({ title: 'User unblocked', description: 'They may appear again based on your settings.' });
    } catch (e) {
      console.error('Unblock failed', e);
      toast({ title: 'Failed to unblock user', description: 'Please try again', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-warm">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-warm p-4 pt-safe pb-20">
      <div className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="min-w-[44px] min-h-[44px]"
          >
            <ArrowLeft className="w-7 h-7" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-foreground">Settings</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Section */}
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your name and profile picture</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Avatar */}
              <div className="flex justify-center">
                <Avatar className="w-32 h-32">
                  <AvatarImage src={avatarUrl} alt={name} />
                  <AvatarFallback className="text-2xl bg-muted">
                    {name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>

              {/* Username Input */}
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                {canChangeUsername ? (
                  <>
                    <div className="relative">
                      <Input
                        id="username"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                        placeholder="Your unique username"
                        maxLength={30}
                        required
                        className="pr-10"
                      />
                      {checkingUsername && username !== originalUsername && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        </div>
                      )}
                      {!checkingUsername && username.length >= 3 && username !== originalUsername && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {usernameAvailable ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <X className="w-4 h-4 text-destructive" />
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      You can change your username. It can be changed again after {getNextUsernameChangeDate()?.toLocaleDateString()}.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <Input
                        id="username"
                        type="text"
                        value={username}
                        disabled
                        className="pr-10 bg-muted"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Lock className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {usernameLastChanged 
                        ? `Username can be changed again ${formatDistanceToNow(getNextUsernameChangeDate()!, { addSuffix: true })}`
                        : 'Username cannot be changed yet'}
                    </p>
                  </>
                )}
              </div>

              {/* Name Input */}
              <div className="space-y-2">
                <Label htmlFor="name">Display Name</Label>
                {canChangeName ? (
                  <>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your display name"
                      maxLength={100}
                      required
                    />
                    {nameLastChanged && (
                      <p className="text-xs text-muted-foreground">
                        You can change your display name again after {getNextNameChangeDate()?.toLocaleDateString()}.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <Input
                        id="name"
                        type="text"
                        value={name}
                        disabled
                        className="bg-muted pr-10"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Lock className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {nameLastChanged
                        ? `Display name can be changed again ${formatDistanceToNow(getNextNameChangeDate()!, { addSuffix: true })}`
                        : 'Display name cannot be changed yet'}
                    </p>
                  </>
                )}
              </div>

              {/* Status Input */}
              <div className="space-y-3">
                <Label htmlFor="status">Status</Label>
                
                {/* Quick Select Buttons */}
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Quick select:</p>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        variant={status === option.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => setStatus(option.value)}
                        className="text-sm"
                      >
                        {option.label}
                      </Button>
                    ))}
                    {/* Clear button */}
                    {status && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setStatus('')}
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Custom Status Input */}
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Or write your own:</p>
                  <Input
                    id="status"
                    type="text"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    placeholder="Custom status..."
                    maxLength={150}
                  />
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Share what you're currently doing - visible to other users
                </p>
              </div>

              {/* Avatar Upload */}
              <div className="space-y-2">
                <Label>Profile Picture</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('avatar-upload')?.click()}
                    disabled={uploading}
                    className="flex-1"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Custom
                      </>
                    )}
                  </Button>
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Max size: 5MB. Supported: JPG, PNG, WEBP
                </p>
              </div>

              {/* Random Avatars */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Or choose a random avatar</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={generateRandomAvatars}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {randomAvatars.map((url, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleRandomAvatarSelect(index)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        selectedAvatar === index
                          ? 'border-primary shadow-soft scale-105'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <img src={url} alt={`Avatar ${index + 1}`} className="w-full h-full" />
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy Settings Section */}
          <Card>
            <CardHeader>
              <CardTitle>Privacy Settings</CardTitle>
              <CardDescription>Control how your location is shared</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup value={privacyLevel} onValueChange={(value: any) => setPrivacyLevel(value)}>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors">
                    <RadioGroupItem value="off" id="off" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="off" className="font-semibold cursor-pointer">
                        Off
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Don't share your location. You won't appear on the map to others.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors">
                    <RadioGroupItem value="coarse" id="coarse" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="coarse" className="font-semibold cursor-pointer">
                        Coarse
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Share approximate location (~111m precision). Good for privacy while still being discoverable.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors">
                    <RadioGroupItem value="precise" id="precise" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="precise" className="font-semibold cursor-pointer">
                        Precise
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Share exact location. Best for connecting with nearby users.
                      </p>
                    </div>
                  </div>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Map Settings Section */}
          <Card>
            <CardHeader>
              <CardTitle>Map Settings</CardTitle>
              <CardDescription>Control who appears on your map</CardDescription>
            </CardHeader>
            <CardContent>
              <Label>Visible Radius</Label>
              <RadioGroup value={String(radiusLimit)} onValueChange={(value) => setRadiusLimit(Number(value))}>
                <div className="space-y-3 mt-3">
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="1" id="radius-1" />
                    <Label htmlFor="radius-1" className="font-normal cursor-pointer">
                      1 mile
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="2" id="radius-2" />
                    <Label htmlFor="radius-2" className="font-normal cursor-pointer">
                      2 miles
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="5" id="radius-5" />
                    <Label htmlFor="radius-5" className="font-normal cursor-pointer">
                      5 miles
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="10" id="radius-10" />
                    <Label htmlFor="radius-10" className="font-normal cursor-pointer">
                      10 miles
                    </Label>
                  </div>
                </div>
              </RadioGroup>
              <p className="text-sm text-muted-foreground mt-4">
                Only users within this distance will appear on your map.
              </p>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </form>

        {/* Blocked Users */}
        <Card>
          <CardHeader>
            <CardTitle>Blocked Users</CardTitle>
            <CardDescription>Hide specific users from your map and lists</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add block via search */}
            <div className="space-y-2">
              <Label>Block someone</Label>
              <UserSearch
                excludeUserId={user?.id}
                onUserSelect={(uid) => handleBlockUser(uid)}
              />
              {blocking && (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Blocking…
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Blocking prevents the selected user from appearing in your nearby users. It doesn’t notify them.
              </p>
            </div>

            {/* List of blocked users */}
            <div className="space-y-2">
              <Label>Currently blocked</Label>
              {blockedLoading ? (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : blockedUsers.length === 0 ? (
                <div className="text-sm text-muted-foreground">No blocked users.</div>
              ) : (
                <div className="space-y-2">
                  {blockedUsers.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-3 rounded border border-border/60">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={u.avatar_url || undefined} />
                          <AvatarFallback>{(u.name || u.username || '?').slice(0,1).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{u.name || 'Unnamed User'}</div>
                          <div className="text-xs text-muted-foreground">{u.username ? `@${u.username}` : u.id}</div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleUnblockUser(u.id)}>
                        Unblock
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* About & Release */}
        <AppInfo />

        {/* Danger Zone */}
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Irreversible actions that will permanently affect your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Once you delete your account, there is no going back. All your data, including your profile, saved users, and transaction history will be permanently removed.
            </p>
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                    Delete Account
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-4">
                    <p>
                      <strong>This action cannot be undone.</strong> This will permanently delete your account, profile, and all associated data.
                    </p>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-foreground">
                        Type <span className="font-mono bg-muted px-1 py-0.5 rounded">deleteaccount</span> to confirm:
                      </p>
                      <Input
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="deleteaccount"
                        className="font-mono"
                        disabled={isDeleting}
                      />
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'deleteaccount' || isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete Account'
                    )}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
